import * as vp from 'vopay';
import { Badge, Box, Table, Title, Card } from 'vopay';
import { Button, TableFiltersContainer } from 'UI';
import { useEffect, useState } from 'react';
import { subWeeks, format } from 'date-fns';

import { usePartnerInvoices } from '@/providers/PartnerInvoices.provider';
import useFileDownload from '@/hooks/useFiledDownload';
import useFetchPartnerInvoicePdf from '@/hooks/useApi/useFetchPartnerInvoicePdf';
import useFetchPartnerStatementPdf from '@/hooks/useApi/useFetchPartnerStatementPdf';
import InvoicesForm from './InvoicesForm';
import { useNavMenu } from '@/providers/NavMenu.provider';
import { usePermissions } from '@/providers/Permissions.provider';
import { useTranslation } from 'react-i18next';
import { usePartnerData } from '@/providers/PartnerData.provider';

type PartnerInvoicesRequestType = {
  fromDate: string;
  toDate: string;
  currency: vp.SupportedCurrencies | 'all' | '';
};

const InvoicesList = (): React.JSX.Element => {
  const {
    response: { isRoleViewOnly },
  } = usePartnerData();

  const { getModuleTitle } = useNavMenu();
  const { downloadPdf } = useFileDownload();
  const { permissions } = usePermissions();
  const { response, refreshPartnerInvoices } = usePartnerInvoices();
  const [responseInvoicePdf, fetchInvoicePdf] = useFetchPartnerInvoicePdf({});
  const [responseStatementPdf, fetchStatementPdf] = useFetchPartnerStatementPdf({});
  const [currentInvoice, setCurrentInvoice] = useState('');
  const [currentStatement, setCurrentStatement] = useState('');
  const [invoiceID, setInvoiceID] = useState('');
  const [filtersApplied, setFiltersApplied] = useState(false);
  const { t } = useTranslation('translations', { keyPrefix: 'Invoices.List' });

  useEffect(() => {
    if (responseInvoicePdf) {
      if (permissions.PartnerBillsAccount) {
        downloadPdf(responseInvoicePdf.invoicePdf, currentInvoice);
      }

      if (permissions.ShowPartnerIncome) {
        setCurrentStatement(`statement-pdf-${invoiceID}`);
        fetchStatementPdf({ invoiceId: invoiceID });
      }
    }
  }, [responseInvoicePdf]);

  useEffect(() => {
    if (responseStatementPdf) downloadPdf(responseStatementPdf.StatementPDF, currentStatement);
  }, [responseStatementPdf]);

  useEffect(() => {
    refreshPartnerInvoices({ fromDate: '', toDate: '', currency: '' });
  }, []);

  const search = (values: PartnerInvoicesRequestType) => {
    refreshPartnerInvoices({
      fromDate: values.fromDate,
      toDate: values.toDate,
      currency: values.currency === 'all' ? '' : values.currency,
    });

    const startDate = format(subWeeks(new Date(), 1), 'yyyy-MM-dd');
    const endDate = format(new Date(), 'yyyy-MM-dd');
    setFiltersApplied(values.fromDate !== startDate || values.toDate !== endDate || !!values.currency);
  };

  return (
    <div>
      <Box mb="md" jc="sb" flx="col">
        <Title title={getModuleTitle()} />
      </Box>

      <Card
        style={{
          padding: '16px',
        }}
      >
        <TableFiltersContainer filtersApplied={filtersApplied}>
          <TableFiltersContainer.Trigger />

          <TableFiltersContainer.Content>
            <InvoicesForm type={getModuleTitle()} search={search} />
          </TableFiltersContainer.Content>
        </TableFiltersContainer>

        <Table {...buildTableProps()} />
      </Card>
    </div>
  );

  function buildTableProps(): vp.TableProps<vp.PartnerInvoice> {
    return {
      id: 'statements-invoice-list',
      version: '2',
      zebra: true,
      columns: [
        {
          id: 'invoiceNumber',
          header: t('Table.invoiceNumber'),
          align: 'left',
          field: 'InvoiceNumber',
        },
        {
          id: 'invoiceType',
          header: t('Table.invoiceType'),
          align: 'left',
          field: 'InvoiceType',
        },
        {
          id: 'paymentStatus',
          header: t('Table.paymentStatus'),
          align: 'left',
          template: ({ data }) => (
            <Badge
              label={vp.capitalizeFirstLetter(data.PaymentStatus)}
              color={getPaymentStatusColor(data.PaymentStatus)}
              hasIcon
              width="100px"
            />
          ),
        },
        {
          id: 'period',
          header: t('Table.period'),
          align: 'left',
          template: ({ data }) => `${data.FromDate} — ${data.ToDate}`,
        },
        {
          id: 'currency',
          header: t('Table.currency'),
          align: 'left',
          field: 'CurrencyShortForm',
        },
        {
          id: 'dueDate',
          header: t('Table.dueDate'),
          align: 'left',
          field: 'DueDate',
        },
        {
          id: 'downloadPdf',
          header: '',
          align: 'right',
          template: ({ data }) => buildDownloadButton(data),
        },
      ],
      rows: getPartnerInvoices().map((invoice) => ({
        id: invoice.InvoiceNumber,
        data: invoice,
      })),
    };

    function getPartnerInvoices(): vp.PartnerInvoice[] {
      return response?.partnerInvoices || [];
    }

    function buildDownloadButton(invoice: vp.PartnerInvoice) {
      return (
        <Button
          id={`statement-invoice-pdf-download-${invoice.InvoiceID}`}
          size="small"
          disabled={isRoleViewOnly}
          onClick={() => {
            setCurrentInvoice(`invoice-pdf-${invoice.InvoiceID}`);
            fetchInvoicePdf({ invoiceId: invoice.InvoiceID });
            setInvoiceID(invoice.InvoiceID);
          }}
        >
          {permissions.ShowPartnerIncome
            ? permissions.PartnerBillsAccount
              ? t('Table.downloadButtonInvoicesStatements')
              : t('Table.downloadButtonStatements')
            : t('Table.downloadButtonInvoices')}
        </Button>
      );
    }

    function getPaymentStatusColor(status: string): 'positive' | 'pending' | 'neutral' {
      const active = 'positive' as const;
      const inactive = 'neutral' as const;
      const pending = 'neutral' as const;

      return (
        {
          paid: active,
          pending,
          void: inactive,
        }[status] || inactive
      );
    }
  }
};

export default InvoicesList;
